<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import { ClientServiceContainer } from '../services/ClientServiceContainer';

const props = defineProps<{
  services: ClientServiceContainer;
}>();

const mouseX = ref(0);
const mouseY = ref(0);
const worldX = ref(0);
const worldY = ref(0);
const tileX = ref(0);
const tileY = ref(0);

// Current scene tile dimensions
const sceneTileWidth = ref(16);
const sceneTileHeight = ref(16);

/** Represents a label on a graph axis */
interface AxisLabel {
  coord: number; // tile or pixel coordinate value
  pos: number;   // screen position in pixels
}

const xLabels = ref<AxisLabel[]>([]);
const yLabels = ref<AxisLabel[]>([]);

const isVisible = computed(() => {
  return props.services.debug.state.showMouseCoordinates || props.services.debug.state.showEdgeCoordinates;
});

const showMouse = computed(() => props.services.debug.state.showMouseCoordinates);
const showEdges = computed(() => props.services.debug.state.showEdgeCoordinates);

/**
 * Updates mouse display state based on screen coordinates.
 */
const updateMouseState = (event: MouseEvent) => {
  mouseX.value = event.clientX;
  mouseY.value = event.clientY;
  updateCoordinates(event.clientX, event.clientY);
};

/**
 * Translates screen coordinates to world and tile coordinates.
 */
const updateCoordinates = (screenX: number, screenY: number) => {
  const { render, localPlayer, clientWorld, chunkData } = props.services;
  const stage = render.stage;
  
  if (!stage) return;

  // Convert screen coordinates to world space using the stage transform (camera applied)
  const worldPos = stage.toLocal({ x: screenX, y: screenY });
  worldX.value = Math.floor(worldPos.x);
  worldY.value = Math.floor(worldPos.y);

  // Derive tile coordinates from world position
  const world = clientWorld.getWorld();
  const playerTransform = localPlayer.entityId !== undefined 
    ? world.getComponent(localPlayer.entityId, 'Transform') 
    : null;
  const currentSceneId = playerTransform?.sceneId;

  if (currentSceneId) {
    const metadata = chunkData.getSceneMetadata(currentSceneId);
    if (metadata) {
      sceneTileWidth.value = metadata.tileWidth;
      sceneTileHeight.value = metadata.tileHeight;
      tileX.value = Math.floor(worldX.value / metadata.tileWidth);
      tileY.value = Math.floor(worldY.value / metadata.tileHeight);
    }
  }
};

let rafId: number | null = null;

/**
 * Updates the axis labels periodically to keep them in sync with camera movement/zooming.
 * We use requestAnimationFrame as these labels are effectively part of the map viewport.
 */
const updateAxisLabels = () => {
  if (showEdges.value) {
    calculateAxisLabels();
  }

  rafId = requestAnimationFrame(updateAxisLabels);
};

/**
 * Calculates current axis label positions based on camera viewport and zoom.
 */
const calculateAxisLabels = () => {
  const { render, camera, localPlayer, clientWorld, chunkData } = props.services;
  const stage = render.stage;
  
  if (!stage || camera.screenWidth <= 0 || camera.screenHeight <= 0) return;

  const world = clientWorld.getWorld();
  const playerTransform = localPlayer.entityId !== undefined 
    ? world.getComponent(localPlayer.entityId, 'Transform') 
    : null;
  const currentSceneId = playerTransform?.sceneId;

  if (!currentSceneId) return;

  const metadata = chunkData.getSceneMetadata(currentSceneId);
  if (!metadata) return;

  const tw = metadata.tileWidth;
  const th = metadata.tileHeight;
  sceneTileWidth.value = tw;
  sceneTileHeight.value = th;

  // Transform viewport corners into world space to find visible bounds
  const topLeft = stage.toLocal({ x: 0, y: 0 });
  const bottomRight = stage.toLocal({ x: camera.screenWidth, y: camera.screenHeight });

  const startTileX = Math.floor(topLeft.x / tw);
  const endTileX = Math.ceil(bottomRight.x / tw);
  const startTileY = Math.floor(topLeft.y / th);
  const endTileY = Math.ceil(bottomRight.y / th);

  // Determine adaptive step spacing to prevent label crowding when zoomed out
  const screenTw = tw * camera.zoom;
  const screenTh = th * camera.zoom;
  
  const stepX = getAdaptiveStep(screenTw);
  const stepY = getAdaptiveStep(screenTh);

  // Align iterations to step boundaries for stable label indexing
  const alignedStartX = Math.ceil(startTileX / stepX) * stepX;
  const alignedStartY = Math.ceil(startTileY / stepY) * stepY;

  const newXLabels: AxisLabel[] = [];
  const newYLabels: AxisLabel[] = [];

  for (let tx = alignedStartX; tx <= endTileX; tx += stepX) {
    const screenPos = stage.toGlobal({ x: tx * tw, y: 0 }).x;
    newXLabels.push({ coord: tx, pos: screenPos });
  }

  for (let ty = alignedStartY; ty <= endTileY; ty += stepY) {
    const screenPos = stage.toGlobal({ x: 0, y: ty * th }).y;
    newYLabels.push({ coord: ty, pos: screenPos });
  }

  xLabels.value = newXLabels;
  yLabels.value = newYLabels;
};

/**
 * Determines an appropriate step interval (number of tiles) given current screen spacing.
 */
const getAdaptiveStep = (screenTileDim: number): number => {
  if (screenTileDim >= 20) return 1;
  if (screenTileDim >= 10) return 5;
  return 10;
};

// Optimization: Start/stop listeners and RAF based on debug visibility
watch(showMouse, (enabled) => {
  if (enabled) {
    window.addEventListener('mousemove', updateMouseState);
  } else {
    window.removeEventListener('mousemove', updateMouseState);
  }
}, { immediate: true });

watch(showEdges, (enabled) => {
  if (enabled) {
    if (rafId === null) {
      rafId = requestAnimationFrame(updateAxisLabels);
    }
  } else {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    // Clear labels when hidden
    xLabels.value = [];
    yLabels.value = [];
  }
}, { immediate: true });

onMounted(() => {
  // Watchers handle the initial state due to immediate: true
});

onUnmounted(() => {
  window.removeEventListener('mousemove', updateMouseState);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
  }
});
</script>

<template>
  <div
    v-if="isVisible"
    class="debug-coordinates-container"
  >
    <!-- Tooltip following mouse cursor -->
    <div 
      v-if="showMouse"
      class="mouse-tooltip"
      :style="{ left: mouseX + 15 + 'px', top: mouseY + 15 + 'px' }"
    >
      <div class="coord-row">
        <span class="label">Tile:</span>
        <span class="value">{{ tileX }}, {{ tileY }}</span>
      </div>
      <div class="coord-row">
        <span class="label">Pixel:</span>
        <span class="value">{{ worldX }}, {{ worldY }}</span>
      </div>
    </div>

    <!-- Graph-style axes on the viewport edges -->
    <template v-if="showEdges">
      <!-- Top Axis: Tile X coordinates -->
      <div 
        v-for="label in xLabels" 
        :key="'top-' + label.coord"
        class="axis-label top"
        :style="{ left: label.pos + 'px' }"
      >
        <div class="tick" />
        <span class="value">{{ label.coord }}</span>
      </div>

      <!-- Left Axis: Tile Y coordinates -->
      <div 
        v-for="label in yLabels" 
        :key="'left-' + label.coord"
        class="axis-label left"
        :style="{ top: label.pos + 'px' }"
      >
        <div class="tick" />
        <span class="value">{{ label.coord }}</span>
      </div>

      <!-- Bottom Axis: World Pixel X coordinates -->
      <div 
        v-for="label in xLabels" 
        :key="'bottom-' + label.coord"
        class="axis-label bottom"
        :style="{ left: label.pos + 'px' }"
      >
        <div class="tick" />
        <span class="value">{{ label.coord * sceneTileWidth }}</span>
      </div>

      <!-- Right Axis: World Pixel Y coordinates -->
      <div 
        v-for="label in yLabels" 
        :key="'right-' + label.coord"
        class="axis-label right"
        :style="{ top: label.pos + 'px' }"
      >
        <div class="tick" />
        <span class="value">{{ label.coord * sceneTileHeight }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.debug-coordinates-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 2000000;
  font-family: 'Inter', monospace;
  font-size: 10px;
  color: #00ff00;
}

.mouse-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(0, 255, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.coord-row {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.label {
  opacity: 0.7;
  font-weight: bold;
}

.value {
  font-weight: normal;
}

.axis-label {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 2px;
  pointer-events: none;
}

/* Tick marks at axis boundaries */
.tick {
  background: #00ff00;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
}

.axis-label.top, .axis-label.bottom {
  flex-direction: column;
  width: 0;
  overflow: visible;
  white-space: nowrap;
}

.axis-label.left, .axis-label.right {
  flex-direction: row;
  height: 0;
  overflow: visible;
  white-space: nowrap;
}

.axis-label.top {
  top: 0;
}
.axis-label.top .tick { width: 1px; height: 8px; }

.axis-label.bottom {
  bottom: 0;
}
.axis-label.bottom .tick { width: 1px; height: 8px; order: 2; }

.axis-label.left {
  left: 0;
}
.axis-label.left .tick { width: 8px; height: 1px; }

.axis-label.right {
  right: 0;
}
.axis-label.right .tick { width: 8px; height: 1px; order: 2; }

.axis-label .value {
  background: rgba(0, 0, 0, 0.6);
  padding: 0 2px;
  border-radius: 2px;
  text-shadow: 1px 1px 1px black;
}
</style>
